import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

type Shape = Record<string, string | number | boolean | undefined>

/**
 * Filter state kept in the URL (?status=OPEN&page=2) so a filtered list can be shared, bookmarked
 * and survives a reload. Values are typed from `defaults`: numbers parse as numbers, booleans as
 * 'true'/'false', strings stay strings; '' / undefined removes the key. Changing any filter other
 * than `page` resets `page` to 1.
 */
export function useUrlState<T extends Shape>(defaults: T) {
  const [params, setParams] = useSearchParams()
  const key = JSON.stringify(defaults)

  const state = useMemo(() => {
    const d = JSON.parse(key) as T
    const out: Record<string, unknown> = { ...d }
    for (const k of Object.keys(d)) {
      const raw = params.get(k)
      if (raw === null) continue
      const def = d[k]
      if (typeof def === 'number') out[k] = Number(raw) || def
      else if (typeof def === 'boolean' || (def === undefined && (raw === 'true' || raw === 'false')))
        out[k] = raw === 'true'
      else out[k] = raw
    }
    return out as T
  }, [params, key])

  const set = useCallback(
    (patch: Partial<T>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const d = JSON.parse(key) as T
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === '' || v === d[k]) next.delete(k)
            else next.set(k, String(v))
          }
          if (!('page' in patch) && 'page' in d) next.delete('page')
          return next
        },
        { replace: true },
      )
    },
    [setParams, key],
  )

  const reset = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const k of Object.keys(JSON.parse(key) as T)) next.delete(k)
        return next
      },
      { replace: true },
    )
  }, [setParams, key])

  const active = useMemo(() => {
    const d = JSON.parse(key) as T
    return Object.keys(d).some((k) => k !== 'page' && params.get(k) !== null)
  }, [params, key])

  return [state, set, { reset, active }] as const
}
